import { ReportPeriod, ReportPeriodKey } from '../models';
import moment from 'moment';
import { TsDateRange } from 'tsui/lib/ts-date-range/ts-date-range.component';

export namespace ReportPeriodUtils {
  export const reportPeriodValues = Object.freeze({
    last_month: {
      key: 'last_month',
      start_date: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
      end_date: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
    },
    quarter_to_date: {
      key: 'quarter_to_date',
      start_date: moment().startOf('quarter').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    },
    last_quarter: {
      key: 'last_quarter',
      start_date: moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'),
      end_date: moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'),
    },
    semester_to_date: {
      key: 'semester_to_date',
      start_date:
        moment().month() < 6
          ? moment().startOf('year').format('YYYY-MM-DD')
          : moment().month(6).startOf('month').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    },
    last_semester: {
      key: 'last_semester',
      start_date:
        moment().month() < 6
          ? moment().subtract(1, 'year').month(6).startOf('month').format('YYYY-MM-DD')
          : moment().startOf('year').format('YYYY-MM-DD'),
      end_date:
        moment().month() < 6
          ? moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
          : moment().month(5).endOf('month').format('YYYY-MM-DD'),
    },
    year_to_date: {
      key: 'year_to_date',
      start_date: moment().startOf('year').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    },
    last_year: {
      key: 'last_year',
      start_date: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      end_date: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
    },
    current_year: {
      key: 'current_year',
      start_date: moment().startOf('year').format('YYYY-MM-DD'),
      end_date: moment().endOf('year').format('YYYY-MM-DD'),
    },
    custom: {
      key: 'custom',
      start_date: null,
      end_date: null,
    },
  });

  export const reportPeriodLabels: Readonly<{ [key in keyof typeof reportPeriodValues]: string }> = Object.freeze({
    last_month: 'Last month',
    quarter_to_date: 'Quarter to date',
    last_quarter: 'Last quarter',
    semester_to_date: 'Semester to date',
    last_semester: 'Last semester',
    year_to_date: 'Year to date',
    current_year: 'Current year',
    last_year: 'Last year',
    custom: 'Custom',
  });

  export const buildReportPeriod = (reportPeriod: ReportPeriod): ReportPeriod => {
    if (reportPeriod?.key == null || reportPeriodValues[reportPeriod?.key] == null) {
      console.error('Invalid report period');
      return;
    }

    if (reportPeriod.key === 'custom') {
      return {
        key: 'custom',
        start_date: moment(reportPeriod?.start_date).format('YYYY-MM-DD'),
        end_date: moment(reportPeriod?.end_date).format('YYYY-MM-DD'),
      };
    }
    return reportPeriodValues[reportPeriod?.key];
  };

  export const getDefaultFullDateReportPeriod: () => ReportPeriod = () => reportPeriodValues.last_month;
  export const defaultMonthYearReportPeriod = {
    key: `${moment().subtract(1, 'months').format('MMM YYYY')}`,
    start_date: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
    end_date: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
  };

  export const getReportPeriodBadgeLabel = (reportPeriod: ReportPeriod): string => {
    if (reportPeriod.key === 'custom') {
      return `${moment(reportPeriod.start_date).format('MMM YYYY')} - ${moment(reportPeriod.end_date).format('MMM YYYY')}`;
    }
    return reportPeriodLabels[reportPeriod.key];
  };

  const reportPeriodOptions = Object.keys(reportPeriodLabels).map((key) => ({
    label: reportPeriodLabels[key],
    key: key,
  }));

  export const buildReportPeriodOptions = (exclude: ReportPeriod['key'][] = []) => {
    if (exclude.length === 0) {
      return [...reportPeriodOptions];
    }

    return reportPeriodOptions.filter((option) => !exclude.includes(option.key));
  };

  export const buildCategoriesFromReportPeriod = (reportPeriod: ReportPeriod): string[] => {
    const startDate = moment(reportPeriod.start_date);
    const endDate = moment(reportPeriod.end_date);
    const categories: string[] = [];

    while (startDate.isSameOrBefore(endDate)) {
      categories.push(startDate.format('MMM YYYY'));
      startDate.add(1, 'months');
    }

    return categories;
  };

  export const buildReportPeriodPresets = (exclude: ReportPeriod['key'][] = []): TsDateRange[] => {
    return reportPeriodOptions
      .map((option) => {
        return {
          id: option.key,
          label: option.label,
          range: {
            start: moment(reportPeriodValues[option.key].start_date),
            end: moment(reportPeriodValues[option.key].end_date),
          },
        };
      })
      .filter((option) => !exclude.includes(option.id) && option.id !== 'custom');
  };

  export const isDiffPeriod = (period1: ReportPeriod, period2: ReportPeriod): boolean => {
    if (period1.key === 'custom' || period2.key === 'custom') {
      return period1.start_date !== period2.start_date || period1.end_date !== period2.end_date;
    }

    return period1.key !== period2.key;
  };
}
